/**
 * 分组管理 (Admin) — CRUD + 组员 / 组长
 */

import { authFetch } from './api.js';
import { showToast, formatDate } from './utils.js';
import { registerViewLoader } from './navigation.js';


let managingGroupId = null;
let managingGroup = null;

function formatGroupLeaderLabel(group) {
  if (group.leaderName) return group.leaderName;
  if (group.leaderEmail) return group.leaderEmail;
  if (group.leaderId) return `用户 #${group.leaderId}`;
  return '-';
}

function sameUserId(a, b) {
  return Number(a) === Number(b);
}

function createGroupElement(tagName, className = '', text = undefined) {
  const element = document.createElement(tagName);
  if (className) element.className = className;
  if (text !== undefined) element.textContent = String(text);
  return element;
}

function createGroupMemberChips(group) {
  const members = group.members || [];
  if (!members.length) {
    return createGroupElement('span', 'group-member-empty', '暂无组员');
  }
  const visibleMembers = members.filter((m) => m.role !== 'leader' && m.role !== 'admin');
  if (!visibleMembers.length) {
    return createGroupElement('span', 'group-member-empty', '暂无组员');
  }
  const chips = createGroupElement('div', 'group-member-chips');
  visibleMembers.forEach((member) => {
    const label = member.name || member.email || `用户#${member.id}`;
    const title = member.email ? `${label} · ${member.email}` : label;
    const chip = createGroupElement('span', 'group-member-chip', label);
    chip.title = String(title);
    chips.appendChild(chip);
  });
  return chips;
}

function createGroupStatRow(label, value, title = value) {
  const row = createGroupElement('div', 'group-stat-row');
  row.appendChild(createGroupElement('span', 'group-stat-label-v2', label));
  const valueElement = createGroupElement('span', 'group-stat-value-v2', value);
  valueElement.title = String(title ?? '');
  row.appendChild(valueElement);
  return row;
}

function createGroupActionButton(className, action, id, text) {
  const button = createGroupElement('button', className, text);
  button.type = 'button';
  button.dataset.action = action;
  button.dataset.id = String(id ?? '');
  return button;
}

function createGroupCard(group) {
  const leaderName = formatGroupLeaderLabel(group);
  const memberCount = (group.members || []).filter((member) => member.role !== 'leader' && member.role !== 'admin').length;
  const description = group.description || '暂无描述';
  const icon = group.name?.includes('管理') ? '👑' : (group.name?.includes('运营') ? '🏢' : '👥');
  const card = createGroupElement('div', 'group-card-v2');
  const header = createGroupElement('div', 'group-card-header-v2');
  const title = createGroupElement('span', 'group-card-title-v2', group.name || '未命名分组');
  title.title = String(group.name || '未命名分组');
  header.append(title, createGroupElement('div', 'group-card-icon-v2', icon));
  card.appendChild(header);

  const stats = createGroupElement('div', 'group-card-stats-v2');
  const feishuTableId = group.feishuTableId || group.feishu_table_id || '';
  const syncEnabled = Boolean(group.marketAnalysisSyncEnabled ?? group.market_analysis_sync_enabled);
  stats.append(
    createGroupStatRow('组长', leaderName),
    createGroupStatRow('成员数', memberCount),
    createGroupStatRow('描述', description),
    createGroupStatRow('飞书表', feishuTableId || '未配置', feishuTableId || '未配置'),
    createGroupStatRow('市场分析同步', syncEnabled ? '已启用' : '关闭'),
    createGroupStatRow('创建时间', formatDate(group.createdAt)),
  );
  card.appendChild(stats);

  const members = createGroupElement('div', 'group-members-section-v2');
  members.appendChild(createGroupElement('div', 'group-members-label-v2', '组员'));
  members.appendChild(createGroupMemberChips(group));
  card.appendChild(members);

  const actions = createGroupElement('div', 'group-card-actions-v2');
  actions.append(
    createGroupActionButton('btn btn-ghost-v2', 'manage-group', group.id, '编辑'),
    createGroupActionButton('btn btn-danger-v2', 'delete-group', group.id, '删除'),
  );
  card.appendChild(actions);
  return card;
}

export function initGroups() {
  registerViewLoader('admin-groups', () => loadGroups());

  document.getElementById('create-group-btn')?.addEventListener('click', showCreateGroupModal);
  document.getElementById('create-group-submit')?.addEventListener('click', createGroup);
  document.getElementById('create-group-cancel')?.addEventListener('click', hideCreateGroupModal);

  document.getElementById('group-manage-cancel')?.addEventListener('click', hideManageGroupModal);
  document.getElementById('group-manage-save')?.addEventListener('click', saveManageGroup);
  document.getElementById('manage-group-feishu-validate')?.addEventListener('click', validateGroupFeishuTable);
  document.getElementById('manage-add-member-btn')?.addEventListener('click', addMemberFromSelect);
}

export async function loadGroups() {
  try {
    const response = await authFetch('/api/groups');
    if (!response.ok) throw new Error('Failed to load groups');
    const data = await response.json();
    renderGroupsTable(data.groups);
  } catch (error) {
    console.error('Error loading groups:', error);
    showToast('加载分组列表失败', 'error');
  }
}

function renderGroupsTable(groups) {
  const tbody = document.getElementById('groups-tbody');
  if (!groups?.length) {
    const row = document.createElement('tr');
    const cell = document.createElement('td');
    cell.colSpan = 6;
    const empty = createGroupElement('div', 'groups-empty-card');
    empty.appendChild(createGroupElement('div', 'groups-empty-icon', '🏢'));
    empty.appendChild(createGroupElement('p', '', '暂无分组，点击上方“新建分组”创建'));
    cell.appendChild(empty);
    row.appendChild(cell);
    tbody.replaceChildren(row);
    return;
  }

  // 卡片网格：在 tbody 里用单行单列包裹卡片网格容器（保留 HTML 结构兼容）
  const row = document.createElement('tr');
  const cell = createGroupElement('td', 'groups-cards-cell');
  cell.colSpan = 6;
  const grid = createGroupElement('div', 'groups-cards-grid');
  grid.replaceChildren(...groups.map((group) => createGroupCard(group)));
  cell.appendChild(grid);
  row.appendChild(cell);
  tbody.replaceChildren(row);
}

function showCreateGroupModal() {
  document.getElementById('create-group-modal').hidden = false;
  document.getElementById('new-group-name').value = '';
  document.getElementById('new-group-desc').value = '';
  document.getElementById('new-group-name').focus();
}

function hideCreateGroupModal() {
  document.getElementById('create-group-modal').hidden = true;
}

export async function createGroup() {
  const name = document.getElementById('new-group-name').value.trim();
  const description = document.getElementById('new-group-desc').value.trim();
  if (!name) {
    showToast('请输入分组名称', 'error');
    return;
  }
  try {
    const response = await authFetch('/api/groups', {
      method: 'POST',
      body: JSON.stringify({ name, description: description || null }),
    });
    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || '创建失败');
    }
    hideCreateGroupModal();
    showToast('分组创建成功', 'success');
    await loadGroups();
  } catch (error) {
    showToast(error.message || '创建分组失败', 'error');
  }
}

export async function deleteGroup(groupId) {
  if (!confirm('确定要删除该分组吗？需要先移除所有成员。')) return;
  try {
    const response = await authFetch(`/api/groups/${groupId}`, { method: 'DELETE' });
    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || '删除失败');
    }
    showToast('分组已删除', 'success');
    await loadGroups();
  } catch (error) {
    showToast(error.message || '删除分组失败', 'error');
  }
}

export async function manageGroup(groupId) {
  managingGroupId = Number(groupId);
  try {
    const [groupsRes, usersRes] = await Promise.all([
      authFetch('/api/groups'),
      authFetch('/api/admin/users?page=1&size=500'),
    ]);
    if (!groupsRes.ok) throw new Error('加载分组失败');
    const { groups } = await groupsRes.json();
    managingGroup = groups.find((g) => sameUserId(g.id, managingGroupId));
    if (!managingGroup) {
      showToast('分组不存在', 'error');
      return;
    }

    let allUsers = [];
    if (usersRes.ok) {
      const u = await usersRes.json();
      allUsers = u.users || [];
    }

    document.getElementById('group-manage-title').textContent = `管理分组：${managingGroup.name}`;
    document.getElementById('manage-group-name').value = managingGroup.name || '';
    document.getElementById('manage-group-desc').value = managingGroup.description || '';
    const feishuTableInput = document.getElementById('manage-group-feishu-table-id');
    if (feishuTableInput) {
      feishuTableInput.value = managingGroup.feishuTableId || managingGroup.feishu_table_id || '';
    }
    const syncMode = document.getElementById('manage-group-feishu-sync-mode');
    if (syncMode) {
      syncMode.value = managingGroup.feishuSyncMode
        || managingGroup.feishu_sync_mode
        || ((managingGroup.marketAnalysisSyncEnabled ?? managingGroup.market_analysis_sync_enabled)
          ? 'projection'
          : 'off');
    }
    renderManageMembers(managingGroup);
    renderLeaderSelect(managingGroup);
    renderAddUserSelect(allUsers, managingGroup);
    document.getElementById('group-manage-modal').hidden = false;
  } catch (err) {
    console.error(err);
    showToast(err.message || '打开管理面板失败', 'error');
  }
}

function hideManageGroupModal() {
  document.getElementById('group-manage-modal').hidden = true;
  managingGroupId = null;
  managingGroup = null;
}

async function validateGroupFeishuTable() {
  if (!managingGroupId) return;
  const button = document.getElementById('manage-group-feishu-validate');
  const hint = document.getElementById('manage-group-feishu-validate-hint');
  if (button) button.disabled = true;
  if (hint) hint.textContent = '正在只读检查字段、类型和业务选项…';
  try {
    const response = await authFetch(
      `/api/admin/market-analysis/feishu/validate-group/${managingGroupId}`,
      { method: 'POST' },
    );
    const body = await response.json();
    if (!response.ok) throw new Error(body.error || '飞书字段检查失败');
    const report = body.data || {};
    if (report.valid) {
      const warnings = (report.configurationWarnings || [])
        .map((item) => item.message)
        .filter(Boolean);
      if (hint) {
        hint.textContent = warnings.length
          ? `检查通过：共读取 ${report.fieldCount} 个字段。测试配置提醒：${warnings.join('；')}（同步时跳过审核人字段）`
          : `检查通过：共读取 ${report.fieldCount} 个字段，字段、类型和业务选项均已对齐。`;
      }
      showToast('飞书数据表字段检查通过', 'success');
      return;
    }
    const issues = [
      ...(report.missing || []).map((name) => `缺少字段“${name}”`),
      ...(report.typeMismatches || []).map(
        (item) => `“${item.name}”类型应为 ${item.expected}，当前为 ${item.actual}`,
      ),
      ...(report.optionMismatches || []).map((item) => {
        const missing = item.missingOptions?.length ? `缺少：${item.missingOptions.join('、')}` : '';
        const extra = item.extraOptions?.length ? `多出：${item.extraOptions.join('、')}` : '';
        return `“${item.name}”选项不一致（${[missing, extra].filter(Boolean).join('；')}）`;
      }),
      ...(report.configurationIssues || []).map((item) => item.message),
    ];
    if (hint) hint.textContent = `检查未通过：${issues.join('；')}`;
    showToast('飞书数据表存在字段差异，请查看检查结果', 'error');
  } catch (error) {
    if (hint) hint.textContent = error.message || '飞书字段检查失败';
    showToast(error.message || '飞书字段检查失败', 'error');
  } finally {
    if (button) button.disabled = false;
  }
}

function renderManageMembers(group) {
  const ul = document.getElementById('manage-group-members');
  const members = (group.members || []).filter((m) => m.role !== 'leader' && m.role !== 'admin');
  if (!members.length) {
    ul.replaceChildren(createGroupElement('li', 'manage-group-empty', '暂无组员，请从下方添加'));
    return;
  }
  const rows = members.map((member) => {
    const item = createGroupElement('li', 'manage-group-member');
    const identity = createGroupElement('span');
    identity.appendChild(document.createTextNode(String(member.name || member.email || '')));
    if (member.email) identity.appendChild(createGroupElement('span', 'manage-group-member-email', ` ${member.email}`));
    const remove = createGroupElement('button', 'btn btn-danger btn-sm', '移除');
    remove.type = 'button';
    remove.dataset.action = 'remove-group-member';
    remove.dataset.userId = String(member.id ?? '');
    remove.addEventListener('click', () => removeMember(Number(member.id)));
    item.append(identity, remove);
    return item;
  });
  ul.replaceChildren(...rows);
}

function renderLeaderSelect(group) {
  const sel = document.getElementById('manage-group-leader');
  const members = group.members || [];
  const options = [new Option('无组长', '')];
  members.forEach((member) => {
    const option = new Option(String(member.name || member.email || ''), String(member.id ?? ''));
    option.selected = group.leaderId != null && sameUserId(group.leaderId, member.id);
    options.push(option);
  });
  sel.replaceChildren(...options);
}

function renderAddUserSelect(allUsers, group) {
  const sel = document.getElementById('manage-add-user');
  const memberIds = new Set((group.members || []).map((m) => Number(m.id)));
  const candidates = allUsers.filter((u) => !memberIds.has(Number(u.id)) && u.role !== 'admin');
  const options = [new Option('选择用户加入…', '')];
  candidates.forEach((user) => {
    const inOther = user.groupId && user.groupId !== group.id;
    const hint = inOther ? ' (当前在其他组)' : '';
    options.push(new Option(`${user.name || user.email || ''}${hint}`, String(user.id ?? '')));
  });
  sel.replaceChildren(...options);
}

async function refreshManagingGroup() {
  const res = await authFetch('/api/groups');
  if (!res.ok) return;
  const { groups } = await res.json();
  managingGroup = groups.find((g) => g.id === managingGroupId);
  if (managingGroup) {
    renderManageMembers(managingGroup);
    renderLeaderSelect(managingGroup);
    const usersRes = await authFetch('/api/admin/users?page=1&size=500');
    if (usersRes.ok) {
      const { users } = await usersRes.json();
      renderAddUserSelect(users || [], managingGroup);
    }
  }
  await loadGroups();
}

async function addMemberFromSelect() {
  const userId = Number(document.getElementById('manage-add-user').value);
  if (!userId || !managingGroupId) {
    showToast('请选择要添加的用户', 'error');
    return;
  }
  try {
    const res = await authFetch(`/api/groups/${managingGroupId}/members`, {
      method: 'POST',
      body: JSON.stringify({ userId }),
    });
    if (!res.ok) {
      const data = await res.json();
      throw new Error(data.error || '添加失败');
    }
    showToast('已添加组员', 'success');
    await refreshManagingGroup();
  } catch (err) {
    showToast(err.message || '添加组员失败', 'error');
  }
}

async function removeMember(userId) {
  if (!confirm('确定将该用户移出本组？')) return;
  try {
    const res = await authFetch(`/api/groups/${managingGroupId}/members/${userId}`, { method: 'DELETE' });
    if (!res.ok) {
      const data = await res.json();
      throw new Error(data.error || '移除失败');
    }
    showToast('已移除组员', 'success');
    await refreshManagingGroup();
  } catch (err) {
    showToast(err.message || '移除失败', 'error');
  }
}

export async function saveManageGroup() {
  if (!managingGroupId) return;
  const name = document.getElementById('manage-group-name').value.trim();
  const description = document.getElementById('manage-group-desc').value.trim();
  const leaderVal = document.getElementById('manage-group-leader').value;
  const feishuTableId = document.getElementById('manage-group-feishu-table-id')?.value?.trim() || '';
  const feishuSyncMode = document.getElementById('manage-group-feishu-sync-mode')?.value || 'off';
  if (!name) {
    showToast('分组名称不能为空', 'error');
    return;
  }
  const body = {
    name,
    description: description || null,
    leaderId: leaderVal ? Number(leaderVal) : null,
    feishuTableId: feishuTableId || null,
    marketAnalysisSyncEnabled: feishuSyncMode !== 'off',
    feishuSyncMode,
  };
  try {
    const res = await authFetch(`/api/groups/${managingGroupId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const data = await res.json();
      throw new Error(data.error || '保存失败');
    }
    showToast('分组已保存', 'success');
    hideManageGroupModal();
    await loadGroups();
  } catch (err) {
    showToast(err.message || '保存失败', 'error');
  }
}
